/**
 * `long_text` — one ellipsised line in the grid, a textarea popover to edit (M2 §5.4, §6).
 * `Enter` inserts a newline; `Cmd/Ctrl+Enter` or an outside click commits; `Esc` cancels.
 */
import { getColumnType, type LongTextValue } from '@elizmonday/shared';
import { useRef } from 'react';
import { CellPopover, CellText, useEditorDraft } from '../primitives';
import { defineColumnUi } from '../types';
import { LongTextSettingsDialog } from './SettingsDialog';

const shared = getColumnType('long_text');

/** Measured: the card is 300 wide and the counter reads `<n>/2000`. */
export const LONG_TEXT_PICKER_WIDTH = 300;
export const LONG_TEXT_MAX = 2000;

export const longTextColumnUi = defineColumnUi<LongTextValue, Record<string, unknown>>({
  type: 'long_text',
  icon: 'columnLongText',
  gallery: { group: 'essentials', keywords: ['long text', 'notes', 'paragraph', 'description'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'left',
  groupable: false,

  Cell: ({ value, settings, context }) => {
    const text = shared.toText(value, settings) ?? '';
    return (
      <CellText
        // The grid shows one line; the newline becomes a space so the ellipsis is honest.
        text={text.replaceAll('\n', ' ')}
        align="left"
        search={context.search}
        title={text || undefined}
      />
    );
  },

  /**
   * Rebuilt from `discovery/ui-reference/editors/long_text/editor.json`: a **300 × 107** card,
   * radius 4, padding `4px 4px 0 0`; a **294 × 77** textarea, radius 3, padding `0 8px 4px`,
   * 14px/400; and a 294 × 24 character-count row whose 12px counter reads `71/2000` — which
   * is where `LONG_TEXT_MAX` comes from. Behaviour from `inline-behaviour.json`: the caret
   * lands at **position 0**, `Enter` inserts a newline (it does not commit), `Escape` reverts
   * and an outside click commits.
   */
  Editor: ({ value, settings, initialInput, onCommit, onCancel }) => {
    /*
     * **P-282.** The note follows the store while it is untouched — a paste on the row while
     * this textarea is open replaces it rather than leaving the person reading the old note
     * under a toast that says the paste landed — and an outside click on a note nobody touched
     * writes nothing (it used to commit the seed back, which is how the pasted value was lost).
     */
    const editor = useEditorDraft(shared.toText(value, settings) ?? '', initialInput);
    const draft = editor.draft;
    const committed = useRef(false);
    const commit = () => {
      if (committed.current) return;
      committed.current = true;
      onCommit(draft.trim() === '' ? null : ({ text: draft } as LongTextValue));
    };
    const dismiss = () => editor.dismiss(commit, onCancel);
    /*
     * **Found while auditing the popover editors for P-282.** The caret rule below is *on open*,
     * and an inline `ref` callback is re-invoked on **every** render (React detaches it with
     * `null` and re-attaches it whenever its identity changes, which for an arrow written in
     * JSX is every time). So `setSelectionRange(0, 0)` ran after every keystroke and a note
     * typed into the grid came out reversed — `ehT` for `The`. It has to fire once, at mount.
     */
    const opened = useRef(false);
    return (
      <CellPopover
        label="Edit note"
        width={LONG_TEXT_PICKER_WIDTH}
        variant="long-text"
        onDismiss={dismiss}
        onCancel={onCancel}
      >
        <textarea
          ref={(node) => {
            if (!node || opened.current) return;
            opened.current = true;
            node.focus();
            // monday opens with the caret at the start, not with the note selected.
            node.setSelectionRange(0, 0);
          }}
          className="ez-cell__textarea"
          data-testid="long-text-input"
          aria-label="Note"
          maxLength={LONG_TEXT_MAX}
          value={draft}
          onChange={(event) => editor.set(event.target.value)}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === 'Escape') {
              committed.current = true;
              onCancel();
            }
            // `Enter` is a newline here — monday's textarea grew a line rather than closing.
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) commit();
          }}
        />
        <div className="ez-long-text__count" data-testid="long-text-count">
          {`${String(draft.length)}/${String(LONG_TEXT_MAX)}`}
        </div>
      </CellPopover>
    );
  },

  SettingsDialog: LongTextSettingsDialog,

  fromClipboard(text, settings) {
    return shared.parseValue(text, settings) as LongTextValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf() {
    // A free-text paragraph is never a useful facet or group-by bucket.
    return [];
  },
});
